// Participant groups ("Gruppe") and roles (Orga/Admin) — mirrors the backend contracts
// (AveOrga.Shared.Contracts.ParticipantGroups). Enums arrive as strings (JsonStringEnumConverter);
// numbers are accepted as a fallback, so every reader goes through the tolerant normalizers below.
//
// Naming rule: JSON fields are participantGroup… — never group… (that is the team number EventPlayer.group
// and the multi-person sign-up groupToken/groupSize).

// The only system group: a player without a membership is a guest.
export const GUEST_SYSTEM_KEY = 'guest';

// Palette (ColorKey → CSS token --participant-group-color-<key>, light + dark in variables.css).
// PARTICIPANT_GROUP_COLOR_KEYS is the base palette without the default role colors negative/warning;
// TEMPLATE_COLOR_KEYS is the whole palette. The group picker offers the whole palette minus the colors
// the roles currently use (groupColorKeys), because a role overrides the group color of the dot.
export const PARTICIPANT_GROUP_COLOR_KEYS = [
  'neutral', 'positive', 'accent', 'purple', 'teal', 'pink', 'indigo', 'slate', 'brown', 'lime',
];
export const TEMPLATE_COLOR_KEYS = [...PARTICIPANT_GROUP_COLOR_KEYS, 'warning', 'negative'];
const KNOWN_COLOR_KEYS = new Set(TEMPLATE_COLOR_KEYS);
// Role colors: always filled, so no "neutral" (empty ring).
export const ROLE_COLOR_CHOICES = TEMPLATE_COLOR_KEYS.filter(k => k !== 'neutral');

export const COLOR_KEY_LABELS = {
  neutral: 'Ohne Farbe',
  positive: 'Grün',
  accent: 'Blau',
  purple: 'Lila',
  teal: 'Petrol',
  pink: 'Pink',
  indigo: 'Indigo',
  slate: 'Schiefer',
  brown: 'Braun',
  lime: 'Oliv',
  warning: 'Gelb',
  negative: 'Rot',
};

export const normalizeColorKey = (key) => (KNOWN_COLOR_KEYS.has(key) ? key : 'neutral');
export const colorVar = (key) => `var(--participant-group-color-${normalizeColorKey(key)})`;

// ── Roles ──────────────────────────────────────────────────────────────────────────────────────
// Name, dot color, event leadership and salary of a role are configured in /groups (RoleSetting,
// GET /Role/GetSettings). hooks/useRoleSettings loads them once and publishes them into the registry
// below; the constants are only the seeded defaults used until then (or when loading fails). Read role
// names/colors through roleLabel/roleColorKey — never through the constants directly.
export const ROLE = { Organizer: 'Organizer', Admin: 'Admin' };
export const ROLE_ORDER = [ROLE.Organizer, ROLE.Admin];
export const ROLE_COLOR_KEY = { Organizer: 'negative', Admin: 'warning' };
export const ROLE_LABEL = { Organizer: 'Orga', Admin: 'Admin' };
// Fixed permissions per role (not configurable yet) — "Rolle setzen" popup and role editor.
export const ROLE_PERMISSIONS_TEXT = {
  Organizer: 'Verwaltet die Anwesenheiten der Spieltage und fügt Teilnehmer hinzu; eingeschränkter Zugriff auf Statistiken und Wiki-Seiten.',
  Admin: 'Voller Zugriff auf alle Funktionen der Verwaltung – einschließlich Preise & Löhne, Gruppen und Rollen, Rollenvergabe, Prozesse und Spieltage.',
};

// Role (string or number, Domain/Enums/Role.cs) → 'Organizer' | 'Admin' | null.
export function normalizeRole(value) {
  if (value === 'Organizer' || value === 3) return ROLE.Organizer;
  if (value === 'Admin' || value === 4) return ROLE.Admin;
  return null;
}

// RoleSettingResponse → { role, name, colorKey, canLeadEvents, leaderSalary }; null for unknown roles.
export function normalizeRoleSetting(raw) {
  const role = normalizeRole(raw?.role);
  if (!role) return null;
  const salary = Number(raw.leaderSalary);
  return {
    role,
    name: String(raw.name ?? '').trim() || ROLE_LABEL[role],
    colorKey: KNOWN_COLOR_KEYS.has(raw.colorKey) ? raw.colorKey : ROLE_COLOR_KEY[role],
    canLeadEvents: raw.canLeadEvents !== false,
    leaderSalary: Number.isFinite(salary) && salary >= 0 ? salary : 0,
  };
}

// List in fixed role order (Orga, Admin), one entry per role.
export function normalizeRoleSettings(list) {
  const byRole = new Map();
  (Array.isArray(list) ? list : []).forEach(raw => {
    const setting = normalizeRoleSetting(raw);
    if (setting && !byRole.has(setting.role)) byRole.set(setting.role, setting);
  });
  return ROLE_ORDER.filter(role => byRole.has(role)).map(role => byRole.get(role));
}

let roleRegistry = new Map();

// Called by hooks/useRoleSettings whenever the settings are (re)loaded or saved.
export function applyRoleSettings(list) {
  roleRegistry = new Map((Array.isArray(list) ? list : []).map(s => [s.role, s]));
}

export function roleLabel(role) {
  const r = normalizeRole(role);
  if (!r) return '';
  return roleRegistry.get(r)?.name || ROLE_LABEL[r];
}

// Dot color of a role (overrides the group color); null without a role.
export function roleColorKey(role) {
  const r = normalizeRole(role);
  if (!r) return null;
  return roleRegistry.get(r)?.colorKey ?? ROLE_COLOR_KEY[r];
}

// May a person with this role lead events (RoleSetting.CanLeadEvents)? The server decides per participant
// (EventParticipantResponse.canLead); this is only for hints before an action.
export function roleCanLeadEvents(role) {
  const r = normalizeRole(role);
  if (!r) return false;
  return roleRegistry.get(r)?.canLeadEvents ?? true;
}

// Colors currently used by the roles (configured or default), in role order.
export const roleColorKeys = () => ROLE_ORDER.map(roleColorKey);

// Colors a group may choose: the whole palette minus the role colors (the dot shows the role color
// instead of the group color, so both would look alike). `current` stays selectable, so a group whose
// color was taken by a role later still shows its selection.
export function groupColorKeys(current = null) {
  const taken = new Set(roleColorKeys());
  return TEMPLATE_COLOR_KEYS.filter(k => !taken.has(k) || k === current);
}

// ── Enums ──────────────────────────────────────────────────────────────────────────────────────
const enumNormalizer = (names) => (value, fallback = names[0]) => {
  if (typeof value === 'number') return names[value] ?? fallback;
  return names.includes(value) ? value : fallback;
};

export const normalizeValidityMode = enumNormalizer(['CalendarYear', 'OpenEnded', 'DateRange']);
export const normalizeSignupChannel = enumNormalizer(['Form', 'PersonalLink']);
export const normalizeSelfServiceBooking = enumNormalizer(['Inbox', 'Immediate']);
// MembershipOrigin: Manual = 0, RoleAssignment = 3.
export const normalizeMembershipOrigin = (value) =>
  (value === 'RoleAssignment' || value === 3 ? 'RoleAssignment' : 'Manual');
export const normalizeReleaseSource = (value) =>
  (value == null ? null : enumNormalizer(['ProcessStep', 'Manual'])(value));
export const normalizeLifecycle = enumNormalizer(['Draft', 'Active', 'Completed', 'Cancelled']);

export const VALIDITY_MODE_LABEL = {
  CalendarYear: 'Kalenderjahr',
  OpenEnded: 'Unbefristet',
  DateRange: 'Freier Zeitraum',
};

export const SIGNUP_CHANNEL_LABEL = {
  Form: 'Formular',
  PersonalLink: 'Persönlicher Link',
};

export const SELF_SERVICE_BOOKING_LABEL = {
  Inbox: 'Über die Inbox',
  Immediate: 'Sofort gebucht',
};

export const MEMBERSHIP_ORIGIN_LABEL = {
  Manual: 'Manuell',
  RoleAssignment: 'Über Rolle',
};

// ── Groups ─────────────────────────────────────────────────────────────────────────────────────

// Full group (ParticipantGroupResponse) or compact ref (ParticipantGroupRef) → one shape.
export function normalizeParticipantGroup(raw) {
  if (!raw || raw.id == null) return null;
  const isGuest = raw.isGuest === true || raw.systemKey === GUEST_SYSTEM_KEY;
  return {
    ...raw,
    id: Number(raw.id),
    name: raw.name ?? '',
    // The plural as stored (null = none) — the group editor must tell "no plural" from "plural = name".
    // Kept when an already normalized group is normalized again.
    rawPluralName: 'rawPluralName' in raw ? raw.rawPluralName : (raw.pluralName ?? null),
    pluralName: raw.pluralName || raw.name || '',
    colorKey: normalizeColorKey(raw.colorKey),
    icon: raw.icon ?? null,
    isGuest,
    isArchived: raw.isArchived === true,
    systemKey: raw.systemKey ?? (isGuest ? GUEST_SYSTEM_KEY : null),
    sortOrder: Number(raw.sortOrder ?? 0),
    validityMode: normalizeValidityMode(raw.validityMode),
    signupChannel: normalizeSignupChannel(raw.signupChannel),
    selfServiceBooking: normalizeSelfServiceBooking(raw.selfServiceBooking),
  };
}

export function normalizeParticipantGroups(list) {
  return (Array.isArray(list) ? list : [])
    .map(normalizeParticipantGroup)
    .filter(Boolean)
    .sort(compareParticipantGroups);
}

// Display order everywhere: SortOrder (guest is seeded last), then name.
export function compareParticipantGroups(a, b) {
  return (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name, 'de');
}

export const isPersonalLinkGroup = (g) => !!g && normalizeSignupChannel(g.signupChannel) === 'PersonalLink';

// Group + role of a row (participantGroupId / participantGroup, roleSnapshot / role).
// groupsById: Map from useParticipantGroups(). Returns { group, role } — group may be null while the
// group list is not loaded yet and the row only carries the id.
export function resolveGroupAndRole(row, groupsById) {
  if (!row) return { group: null, role: null };
  const role = normalizeRole(row.roleSnapshot ?? row.role ?? null);

  let group = null;
  if (row.participantGroup) group = normalizeParticipantGroup(row.participantGroup);
  const id = row.participantGroupId ?? row.participantGroup?.id;
  if (id != null && groupsById?.get(Number(id))) group = groupsById.get(Number(id));
  return { group, role };
}

// Tooltip/label: "Orga · Gast", "Gast".
export function groupRoleLabel(group, role) {
  const r = roleLabel(role);
  const g = group?.name ?? '';
  if (r && g) return `${r} · ${g}`;
  return r || g;
}

// Released group ids of an event (EventParticipantGroupSettingDto[]). A guest release counts for all.
export function releasedGroupIds(settings) {
  return (Array.isArray(settings) ? settings : []).filter(s => s.released).map(s => Number(s.participantGroupId));
}

// "Freigegeben für: Mitglieder, Gäste" — plural names in display order; '' when nothing is released.
export function releaseSummary(settings, groupsById) {
  const ids = new Set(releasedGroupIds(settings));
  if (!groupsById || ids.size === 0) return '';
  return [...groupsById.values()]
    .filter(g => ids.has(g.id))
    .sort(compareParticipantGroups)
    .map(g => g.pluralName)
    .join(', ');
}
